"use client";

import {
    getLocalFirstInitialData,
    getLocalFirstUserDimensionKey,
    readLocalFirstQueryCache,
    writeLocalFirstQueryCache,
} from "@/lib/query-local-first";
import { useAuthStore } from "@/store/auth";
import { useUserInfo } from "@/store/user-info";
import {
    type InfiniteData,
    type QueryKey,
    useInfiniteQuery,
    type UseInfiniteQueryOptions,
    useQuery,
    type UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

export function useLocalFirstUserScope() {
    const token = useAuthStore( ( state ) => state.token );
    const userId = useUserInfo( ( state ) => state.userId );
    const dimensionKey = useMemo( () => getLocalFirstUserDimensionKey( userId, token ), [ token, userId ] );

    return {
        token,
        userId,
        dimensionKey,
    } as const;
}

export function useLocalFirstCacheEntry<T>( storageKey: string | null | undefined ) {
    const cached = useMemo(
        () =>
            storageKey
                ? readLocalFirstQueryCache<T>( storageKey )
                : undefined,
        [ storageKey ],
    );
    const write = useCallback( ( data: T ) => {
        if ( !storageKey ) {
            return;
        }

        writeLocalFirstQueryCache( storageKey, data );
    }, [ storageKey ] );

    return {
        storageKey,
        cached,
        write,
        ...getLocalFirstInitialData( cached ),
    } as const;
}

type LocalFirstOnData<T> = ( data: T ) => void;

function useLatestRef<T>( value: T ) {
    const ref = useRef( value );
    ref.current = value;
    return ref;
}

type LocalFirstQueryParams<TData, TQueryKey extends QueryKey> =
    & Omit<UseQueryOptions<TData, Error, TData, TQueryKey>, "initialData" | "initialDataUpdatedAt">
    & {
        storageKey: string | null | undefined;
        onData?: LocalFirstOnData<TData>;
    };

export function useLocalFirstQuery<TData, TQueryKey extends QueryKey>(
    params: LocalFirstQueryParams<TData, TQueryKey>,
) {
    const { storageKey, onData, ...queryOptions } = params;
    const { initialData, initialDataUpdatedAt, write } = useLocalFirstCacheEntry<TData>( storageKey );
    const onDataRef = useLatestRef( onData );
    const query = useQuery( {
        ...queryOptions,
        initialData,
        initialDataUpdatedAt,
    } );

    useEffect( () => {
        if ( query.data === undefined || query.isPlaceholderData ) {
            return;
        }

        write( query.data );
        onDataRef.current?.( query.data );
    }, [ query.data, query.isPlaceholderData, write, onDataRef ] );

    return query;
}

type LocalFirstInfiniteQueryParams<TPageData, TQueryKey extends QueryKey, TPageParam> =
    & Omit<
        UseInfiniteQueryOptions<
            TPageData,
            Error,
            InfiniteData<TPageData, TPageParam>,
            TQueryKey,
            TPageParam
        >,
        "initialData" | "initialDataUpdatedAt"
    >
    & {
        storageKey: string | null | undefined;
        onData?: LocalFirstOnData<InfiniteData<TPageData, TPageParam>>;
    };

export function useLocalFirstInfiniteQuery<TPageData, TQueryKey extends QueryKey, TPageParam>(
    params: LocalFirstInfiniteQueryParams<TPageData, TQueryKey, TPageParam>,
) {
    const { storageKey, onData, ...queryOptions } = params;
    const {
        initialData,
        initialDataUpdatedAt,
        write,
    } = useLocalFirstCacheEntry<InfiniteData<TPageData, TPageParam>>( storageKey );
    const onDataRef = useLatestRef( onData );
    const query = useInfiniteQuery<TPageData, Error, InfiniteData<TPageData, TPageParam>, TQueryKey, TPageParam>( {
        ...queryOptions,
        initialData,
        initialDataUpdatedAt,
    } );

    useEffect( () => {
        if ( query.data === undefined || query.isPlaceholderData ) {
            return;
        }

        write( query.data );
        onDataRef.current?.( query.data );
    }, [ query.data, query.isPlaceholderData, write, onDataRef ] );

    return query;
}
